import type {
  SignupQuizContent,
  SignupQuizQuestion,
  SignupQuizWordedQuestion,
  SignupScreeningQuestion,
} from '@galapagos/shared/registration';
import type { CatalogWords } from './catalog-words';

/**
 * Pure rules for the quiz steps. Program keys are matched only by equality
 * against the content's own priority, so the client never learns which
 * program a key names (ADR-015).
 */

/** A choice with its words, as the quiz shows it. */
export interface WordedQuizOption {
  value: string;
  label: string;
  description?: string;
}

/** A question with its words, as the quiz shows it. */
export interface WordedQuizQuestion {
  answerName: string;
  saveAs: SignupQuizQuestion['saveAs'];
  title: string;
  description?: string;
  options: WordedQuizOption[];
}

const QUESTIONS = 'quiz.taps1.questions';

/** The member's first selected program in the content's priority, or null. */
export function primaryQuizProgram(
  question: SignupQuizWordedQuestion,
  selection: readonly string[],
): string | null {
  return question.programPriority.find((programKey) => selection.includes(programKey)) ?? null;
}

/**
 * The question with its words from the signup catalog, keyed by answer name
 * and option value. For a primary program, a line the catalog holds under the
 * question's `programs.<programKey>` replaces the default line; every other
 * line reads the default. A line the catalog does not hold at all (an option
 * description) means the question has none.
 */
export function wordedQuizQuestion(
  words: CatalogWords,
  question: SignupQuizQuestion,
  primaryProgram: string | null = null,
): WordedQuizQuestion {
  const key = `${QUESTIONS}.${question.answerName}`;
  const keys = (path: string) =>
    primaryProgram === null
      ? [`${key}.${path}`]
      : [`${key}.programs.${primaryProgram}.${path}`, `${key}.${path}`];
  const line = (path: string) =>
    keys(path).some((candidate) => words.has(candidate)) ? words.text(keys(path)) : undefined;
  const description = line('description');
  return {
    answerName: question.answerName,
    saveAs: question.saveAs,
    title: words.text(keys('title')),
    ...(description === undefined ? {} : { description }),
    options: question.options.map(({ value }) => {
      const optionDescription = line(`options.${value}.description`);
      return {
        value,
        label: words.text(keys(`options.${value}.label`)),
        ...(optionDescription === undefined ? {} : { description: optionDescription }),
      };
    }),
  };
}

/** The intake session answer for a choice, shaped as the content says it is stored. */
export function quizAnswer(
  question: Pick<SignupQuizQuestion, 'answerName' | 'saveAs'>,
  value: string,
): Record<string, unknown> {
  return { [question.answerName]: question.saveAs === 'list' ? [value] : value };
}

/**
 * The member's TAPS 2 questions, in ask order: the chain of their first
 * selected program in the content's priority. Content with no screening
 * section, no selection, or a main program with no chain asks nothing.
 */
export function screeningQuestions(
  content: SignupQuizContent,
  selection: readonly string[],
): readonly SignupScreeningQuestion[] {
  const screening = content.taps2;
  if (!screening) return [];
  const primary = screening.programPriority.find((programKey) => selection.includes(programKey));
  if (primary === undefined) return [];
  return screening.chains[primary] ?? [];
}
